/**
 * FlowPassPanel — "Polish for readability" action.
 *
 * Runs Opus over the current chapter with a focused craft prompt covering
 * prose rhythm, interiority depth, chapter hook, beat pacing, and grade 7–8
 * readability. Shows a before/after diff so the writer can accept or dismiss.
 */
import React, { useState, useCallback } from 'react';
import { Wand2, X, Check, FileText, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { novelBuildApi } from '../../lib/api';

export function FlowPassPanel({ project, chapter, onAccept, onClose }) {
  const [state, setState] = useState('idle'); // idle | running | done | error
  const [polished, setPolished] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const original = (chapter?.content || '').trim();
  const chapterTitle = chapter?.title || 'this chapter';

  const run = useCallback(async (notesOnly = false) => {
    if (!project?.id || !chapter?.id) {
      toast.error('No chapter selected.');
      return;
    }
    setState('running');
    setPolished('');
    setNotes('');
    try {
      const result = await novelBuildApi.flowPass(project.id, chapter.id, notesOnly);
      if (notesOnly) {
        setNotes(result.notes || '');
        setShowNotes(true);
      } else {
        setPolished(result.polished || '');
      }
      setState('done');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Flow pass failed. Try again.';
      toast.error(msg);
      setState('error');
    }
  }, [project, chapter]);

  const accept = () => {
    if (!polished) return;
    onAccept?.(polished);
    toast.success('Chapter updated with polished prose.');
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border" data-testid="flow-pass-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-primary" />
          <span className="text-[13px] font-semibold">Polish for Readability</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="flow-pass-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Chapter info */}
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
          <div className="text-[13px] text-muted-foreground uppercase tracking-wider mb-0.5">Chapter</div>
          <div className="text-[13px] font-medium truncate">{chapterTitle}</div>
          <div className="text-[13px] text-muted-foreground mt-0.5">
            {original.split(/\s+/).filter(Boolean).length.toLocaleString()} words
          </div>
        </div>

        {/* What this does */}
        {state === 'idle' && (
          <div className="space-y-2 text-[13.5px] text-muted-foreground leading-relaxed">
            <p>Opus reads the draft and improves:</p>
            <ul className="space-y-1 pl-3">
              {[
                'Sentence rhythm — short punches after long builds',
                'Emotional interiority — specific thoughts, not generic feelings',
                'Opening hook — the first line grabs or gets rewritten',
                'Scene beat pacing — slow breath for quiet moments, fast for action',
                'Dialogue voice — every character sounds like themselves',
              ].map((item, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className="text-primary shrink-0">·</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[13.5px]">Plot, characters, and setting are never changed.</p>
          </div>
        )}

        {/* Running state */}
        {state === 'running' && (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <div className="text-[14px]">Opus is reading and polishing…</div>
            <div className="text-[13px]">This takes 30–90 seconds for a full chapter.</div>
          </div>
        )}

        {/* Error state */}
        {state === 'error' && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[13.5px] text-destructive">
            Something went wrong. Check that the chapter has content and try again.
          </div>
        )}

        {/* Done — polished result */}
        {state === 'done' && polished && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] font-medium text-emerald-600 dark:text-emerald-400">
                Polished draft ready
              </span>
              <span className="text-[13px] text-muted-foreground">
                {polished.split(/\s+/).filter(Boolean).length.toLocaleString()} words
              </span>
            </div>

            {/* Polished preview */}
            <div className="rounded-md border border-border bg-muted/20 p-3 max-h-64 overflow-y-auto">
              <pre className="text-[13.5px] leading-relaxed whitespace-pre-wrap font-sans">
                {polished.slice(0, 1200)}{polished.length > 1200 ? '\n\n…' : ''}
              </pre>
            </div>

            {/* Original toggle */}
            <button
              onClick={() => setShowOriginal(v => !v)}
              className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="flow-pass-toggle-original"
            >
              <FileText className="h-3.5 w-3.5" />
              {showOriginal ? 'Hide' : 'Show'} original
              {showOriginal ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showOriginal && (
              <div className="rounded-md border border-border bg-muted/10 p-3 max-h-48 overflow-y-auto opacity-60">
                <pre className="text-[13px] leading-relaxed whitespace-pre-wrap font-sans">
                  {original.slice(0, 900)}{original.length > 900 ? '\n\n…' : ''}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Editorial notes */}
        {notes && (
          <div className="space-y-2">
            <button
              onClick={() => setShowNotes(v => !v)}
              className="flex items-center gap-1.5 text-[13.5px] font-medium text-foreground hover:text-primary transition-colors"
            >
              Editorial notes
              {showNotes ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showNotes && (
              <div className="rounded-md border border-border bg-muted/20 p-3 text-[13.5px] leading-relaxed whitespace-pre-wrap">
                {notes}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-border shrink-0 space-y-2">
        {state === 'done' && polished && (
          <Button
            className="w-full gap-2 h-9"
            onClick={accept}
            data-testid="flow-pass-accept"
          >
            <Check className="h-4 w-4" />
            Accept — replace chapter
          </Button>
        )}
        {(state === 'idle' || state === 'done' || state === 'error') && (
          <div className="flex gap-2">
            <Button
              variant={state === 'done' ? 'outline' : 'default'}
              className="flex-1 gap-2 h-9 text-[14px]"
              onClick={() => run(false)}
              disabled={state === 'running' || !original}
              data-testid="flow-pass-run"
            >
              {state === 'running' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              {state === 'done' ? 'Re-run polish' : 'Polish chapter'}
            </Button>
            <Button
              variant="outline"
              className="flex-1 gap-2 h-9 text-[14px]"
              onClick={() => run(true)}
              disabled={state === 'running' || !original}
              data-testid="flow-pass-notes"
            >
              <FileText className="h-3.5 w-3.5" />
              Notes only
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
